import { Alert, Box, Card, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash-es';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormFactoryWithStandardLayout } from '../../../../../components/Form/FormFactory';
import { CompanyLogoAndName } from '../../../../../components/grid-renderers/CompanyCellRenderer';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import {
  manageAssignCompanyFormSchema,
  ManageCompanyFieldsTypes,
} from '../../../../../schemas/template-assignment-schema';
import { useUpdateCompanyAndState } from '../../../../../services/hooks/useUpdateCompanyAndState';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { cardStyles } from '../../../../../theme/component-styles';
import { hasNonRejectedRespondents } from '../../../../../util/respondentStatus';
import { schemaToFormFields } from '../../../../../util/schema-utils';
import { captureFormValidationError } from '../../../../../util/SentryUtils';
import { useCompanyOnboardedStatus } from '../../../hooks/useRespondentApprovalsEnabled';
import { createFormFromFieldsArray } from '../../../../../view-models/form.view-model';
import { CollapseButton } from '../../../../CompanyProfiles/Summary/components/CollapseButton';
import { StepperFormButtons } from '../../../../Finance2/Forms/StepperFormButtons';
import { selectedKpiCompaniesStateDCJ } from '../AllCompaniesStateJ';
import { currentStepStateTAJ } from './AssignTemplateFormState';
import { FormCardContainer } from './common-styled-components';

import { useCompaniesWithMissingData, useValidateCompanyData } from './useValidateCompanyData';
import { useManageCompanyFormMethods } from './useManageCompanyFormMethods';
import { useAtomValue } from 'jotai';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';

export function AssignTemplateStep2() {
  const companyCount = useAtomValue(selectedKpiCompaniesStateDCJ).length;

  if (companyCount === 0) {
    return <Alert severity='error'>No companies selected</Alert>;
  }
  return <AssignTemplateStep2Bulk />;
}

interface ISingleCompanySettingsFormProps {
  company: ICompanyDataModel;
  setLoading?: Dispatch<SetStateAction<boolean>>;
}
export function SingleCompanySettingsForm({ company, setLoading }: ISingleCompanySettingsFormProps) {
  const updateCompanyAndState = useUpdateCompanyAndState();
  const { companyStatuses } = useCompanyOnboardedStatus(company.id);
  // Starbreaker respondents satisfy the check; demanding a local email would block an FYE-only fix.
  const methods = useManageCompanyFormMethods(company as ManageCompanyFieldsTypes, 'assign', {
    requireRespondents: !hasNonRejectedRespondents(companyStatuses),
  });

  const updateCompany = useMemo(
    () =>
      debounce(async () => {
        if (!company) {
          return false;
        }
        // secondaryRespondents is intentionally still sent: the form round-trips the existing
        // value (it's hidden, not cleared, for consolidated clients) and the backend (FD-3410)
        // owns its deprecation — don't drop it from the PATCH.
        const { fye, respondents, secondaryRespondents } = methods.getValues();
        const isValid = await methods.trigger();
        if (!isValid) {
          return;
        }
        setLoading?.(true);
        await updateCompanyAndState(
          company.id,
          { fye, respondents, secondaryRespondents } as Partial<ICompanyDataModel>,
          false,
          false,
          true
        );
        setLoading?.(false);
      }, 500),
    [company, methods, setLoading, updateCompanyAndState]
  );

  const { watch } = methods;
  useEffect(() => {
    const subscription = watch((value, { type }) => {
      if (type === 'change') {
        updateCompany();
      }
    });
    return () => subscription.unsubscribe();
  }, [updateCompany, watch]);

  return (
    <FormProvider {...methods}>
      <ManageCompanyFields />
    </FormProvider>
  );
}

function ManageCompanyFields() {
  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  // CC Email is hidden for consolidated clients; schema/resolver left intact.
  const fields = schemaToFormFields(manageAssignCompanyFormSchema()).filter(
    (field) => !isConsolidated || field.key !== 'secondaryRespondents'
  );
  const form = createFormFromFieldsArray(fields);
  return <FormFactoryWithStandardLayout form={form} />;
}

export function AssignTemplateStep2Bulk() {
  const selectedCompanies = useAtomValue(selectedKpiCompaniesStateDCJ);
  const invalidCompanies = useCompaniesWithMissingData();
  const validateCompanyData = useValidateCompanyData();

  const cardForms = useMemo(() => {
    const initiallyInvalid = selectedCompanies.filter((c) => !validateCompanyData(c));
    return initiallyInvalid.map((company, i) => {
      return <ExpandableFormContainer companyId={company.id} key={company.id} defaultExpanded={i === 0} />;
    });
  }, [selectedCompanies, validateCompanyData]);

  const stepIsValid = useCallback(() => {
    const isValid = invalidCompanies.size === 0;
    if (!isValid) {
      captureFormValidationError('AssignTemplateStep2', {
        errors: invalidCompanies,
      });
      return Promise.resolve(false);
    }
    return Promise.resolve(isValid);
  }, [invalidCompanies]);

  return (
    <>
      <FormCardContainer>{cardForms}</FormCardContainer>
      <StepperFormButtons stepState={currentStepStateTAJ} stepIsValid={stepIsValid} />
    </>
  );
}
interface IExpandableFormContainerProps {
  companyId: number;
  defaultExpanded?: boolean;
}
export function ExpandableFormContainer({
  companyId,
  defaultExpanded: initialExpanded,
}: IExpandableFormContainerProps) {
  const [expanded, setExpanded] = useState(initialExpanded ?? false);
  const company = useAtomValue(companyStateJ(companyId));
  const validateCompanyData = useValidateCompanyData();
  const [loading, setLoading] = useState(false);
  if (!company) {
    return null;
  }
  const invalid = !validateCompanyData(company);
  return (
    <Card
      sx={{
        ...cardStyles,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: '1rem',
        '&:hover': {
          boxShadow: '0px 4px 10px 0px rgba(44, 46, 49, 0.15)',
        },
      }}
    >
      <CollapseButton expanded={expanded} setExpanded={setExpanded} />
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <CompanyLogoAndName name={company.name} logoUrl={company.logoUrl ?? ''} />
        {loading ? (
          <CircularProgress color='error' size={'1rem'} />
        ) : invalid ? (
          <Typography variant='caption' color='error'>
            {'Missing data'}
          </Typography>
        ) : null}
      </Stack>

      <Box style={{ gridColumn: '1/-1' }}>
        <Collapse in={expanded}>
          {expanded ? (
            <Box sx={{ my: '1rem' }}>
              <SingleCompanySettingsForm company={company} setLoading={setLoading} />
            </Box>
          ) : (
            <div />
          )}
        </Collapse>
      </Box>
    </Card>
  );
}
